import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { FormSelect, SearchableSelect } from '@aimsos/ui';

import FormField from './FormField.jsx';
import OptionsLoadError from './OptionsLoadError.jsx';
import PhoneList from './PhoneList.jsx';
import { fieldErrorText } from './fieldErrors.js';

/** Step 2: the rest of the record. Fields are fixed per type. */
function DetailsStep({
  entityType,
  values,
  errors,
  options,
  optionsLoading = false,
  optionsError = null,
  onRetryOptions,
  owners,
  ownersLoading,
  onSearchOwners,
  onChange,
  phoneHandlers,
}) {
  const { t } = useTranslation('unified-profiles');

  const handleOwnerSelect = useCallback(
    (owner) => {
      onChange('ownerId', owner.id);
      onChange('ownerName', owner.name);
    },
    [onChange],
  );

  // The chosen owner may not be on the page the search returned (the default is
  // the current user), so the trigger is labelled from this fallback.
  const selectedOwner = useMemo(
    () => (values.ownerId ? { id: values.ownerId, name: values.ownerName } : undefined),
    [values.ownerId, values.ownerName],
  );

  const select = (name, labelKey, list) => (
    <FormField id={`create-${name}`} label={t(`create.fields.${labelKey}.label`)} error={fieldErrorText(t, name, errors[name])}>
      <FormSelect
        id={`create-${name}`}
        value={values[name] ?? ''}
        options={list}
        placeholder={t(`create.fields.${labelKey}.placeholder`)}
        disabled={optionsLoading}
        onChange={(value) => onChange(name, value)}
        portalListbox
      />
    </FormField>
  );

  return (
    <div className="flex flex-col gap-6">
      {optionsError ? <OptionsLoadError onRetry={onRetryOptions} /> : null}

      {entityType === 'customers' ? (
        <>
          <PhoneList
            phones={values.phones}
            errors={errors.phones}
            onAdd={phoneHandlers.onAdd}
            onChange={phoneHandlers.onChange}
            onMakePrimary={phoneHandlers.onMakePrimary}
            onRemove={phoneHandlers.onRemove}
          />
          {select('location', 'location', options.locations)}
        </>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {select('industry', 'industry', options.industries)}
          {select('city', 'city', options.cities)}
        </div>
      )}

      <FormField label={t('create.fields.owner.label')}>
        <SearchableSelect
          ariaLabel={t('create.fields.owner.label')}
          options={owners}
          value={values.ownerId}
          selectedOption={selectedOwner}
          isLoading={ownersLoading}
          placeholder={t('create.fields.owner.placeholder')}
          searchPlaceholder={t('create.fields.owner.searchPlaceholder')}
          emptyMessage={t('create.fields.owner.empty')}
          onChange={onSearchOwners}
          onSelect={handleOwnerSelect}
        />
      </FormField>
    </div>
  );
}

export default memo(DetailsStep);
